import React, { useMemo } from "react";

import { Box, Button, Drawer, Tab, Tabs } from "@mui/material";

import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { TimelineEventPanel } from "./timeline-panel-drawer";

import {
  BORDER_RADIUS_MUI,
  DEFAULT_ELEVATION,
  DEFAULT_PADDING,
  PANEL_WIDTH,
  TOOLBAR_HEIGHT,
} from "@/diagramme/config";

import type { FiliationEventWithFiliationStateGroupedByDate } from "@/diagramme/types";

export const TimeLinePanel = ({ data, setCurrentDate, currentDate, onDownloadPdf }: any) => {
  const [selectedTab, setSelectedTab] = React.useState<number | false>(() => data?.length - 1);
  const [open, setOpen] = React.useState(true);

  const toggleDrawer = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const dateInGoodOrder = useMemo(() => {
    return data ? [...data].reverse() : [];
  }, [data]);

  // Mettre à jour selectedTab quand currentDate change
  React.useEffect(() => {
    if (currentDate === "overview") {
      setSelectedTab(false); // Désélectionner les tabs de date
      setOpen(false); // Cacher le détail par défaut en mode Vue d'ensemble
    } else {
      const index = dateInGoodOrder?.findIndex(
        (fd: FiliationEventWithFiliationStateGroupedByDate) => fd.date === currentDate
      );
      if (index !== -1) {
        setSelectedTab(index);
      }
      setOpen(true); // Afficher le détail en mode timeline
    }
  }, [currentDate, dateInGoodOrder]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const selectedDate = dateInGoodOrder[newValue];
    if (selectedDate) {
      setCurrentDate(selectedDate.date);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        {/* Onglets chronologiques */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minHeight: "40px",
            "& .MuiTab-root": {
              minHeight: "40px",
              py: 0,
            },
          }}
        >
          {dateInGoodOrder?.map(
            (fd: FiliationEventWithFiliationStateGroupedByDate, index: number) => (
              <Tab key={index} label={fd.date} />
            )
          )}
        </Tabs>

        {/* Bouton Vue d'ensemble */}
        <Button
          onClick={() => setCurrentDate("overview")}
          variant={currentDate === "overview" ? "contained" : "outlined"}
          color="primary"
          size="small"
          startIcon={<VisibilityOffIcon />}
          sx={{ height: "36px", mx: 1, whiteSpace: "nowrap" }}
        >
          Vue d'ensemble
        </Button>

        {/* Bouton PDF (seulement en mode overview) */}
        {currentDate === "overview" && (
          <Button
            onClick={onDownloadPdf}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            sx={{ height: "36px", mr: 1, whiteSpace: "nowrap" }}
          >
            Télécharger PDF
          </Button>
        )}

        {/* Bouton toggle détail */}
        <Button
          onClick={() => toggleDrawer(!open)}
          variant="contained"
          color="primary"
          size="small"
          sx={{ height: "36px", whiteSpace: "nowrap" }}
        >
          {open ? "Masquer" : "Détail"}
        </Button>
      </Box>

      <Drawer
        anchor={"right"}
        variant="persistent"
        elevation={DEFAULT_ELEVATION}
        open={open}
        onClose={() => toggleDrawer(false)}
        hideBackdrop={true}
        PaperProps={{
          elevation: DEFAULT_ELEVATION,
          sx: {
            top: `${DEFAULT_PADDING + TOOLBAR_HEIGHT}px`,
            right: `${DEFAULT_PADDING}px`,
            bottom: `${DEFAULT_PADDING}px`,
            height: "unset",
            borderRadius: BORDER_RADIUS_MUI,
            width: `${PANEL_WIDTH}px`,
            backgroundColor: "rgba(255,255,255,1)",
          },
        }}
        ModalProps={{ sx: { position: "unset" } }}
      >
        <TimelineEventPanel data={dateInGoodOrder} selectedTab={typeof selectedTab === 'number' ? selectedTab : 0} />
      </Drawer>
    </>
  );
};
